import type { CompletableStats, CompletionSeries, PartnerLearningAssetInsight } from '~kui/components/KUI/Content/Partner/types';

import type { FragmentType } from '~/graphql/generated';
import type {
  AdminPartnerFileFieldsFragment,
  AdminPartnerReferenceFieldsFragment,
  PartnerReferenceFieldsFragment,
  PublicFileFieldsFragment,
} from '~/graphql/generated/graphql';
import type { Partner, PartnerInsights, PartnerInsightSeries, PartnerMeta } from '~/types/partner';

import { publicFileFragment } from '~/graphql/documents/core';
import {
  adminCompletableStatsFragment,
  adminCompletionSeriesFragment,
  adminPartnerAssetInsightFragment,
  adminPartnerFileFragment,
  adminPartnerFragment,
  adminPartnerInsightSeriesFragment,
  adminPartnerInsightsFragment,
  adminPartnerListFragment,
  adminPartnerMetaFragment,
  adminPartnerReferenceFragment,
  partnerMetaFragment,
  partnerReferenceFragment,
} from '~/graphql/documents/partner';
import { useFragment } from '~/graphql/generated';

export const transformPartner = (fragment: FragmentType<typeof adminPartnerFragment>): Partner => {
  const { mapPublicFragmentToUpload } = useV2CoreFileUpload();

  const partner = useFragment(adminPartnerFragment, fragment);
  const meta = partner.meta ? useFragment(adminPartnerMetaFragment, partner.meta) : null;

  return {
    ...partner,
    iconFile: partner.iconFile ? mapPublicFragmentToUpload(useFragment(adminPartnerFileFragment, partner.iconFile)) : null,
    learningAssetCollections: partner.learningAssetCollections ?? [],
    learningAssets: partner.learningAssets ?? [],
    meta,
  };
};

const transformCompletableStats = (fragment: FragmentType<typeof adminCompletableStatsFragment>): CompletableStats => {
  const stats = useFragment(adminCompletableStatsFragment, fragment);

  return {
    completions: stats.completions,
    starts: stats.starts,
    firstCompletedAt: stats.firstCompletedAt ?? null,
    lastCompletedAt: stats.lastCompletedAt ?? null,
  };
};

const toAssetInsight = (fragment: FragmentType<typeof adminPartnerAssetInsightFragment>): PartnerLearningAssetInsight => {
  const asset = useFragment(adminPartnerAssetInsightFragment, fragment);

  return {
    id: asset.id,
    name: asset.name,
    type: asset.type,
    publishedAt: asset.publishedAt ?? null,
    stats: transformCompletableStats(asset.stats),
    collections: asset.learningAssetCollections.map(collection => ({ id: collection.id, name: collection.name })),
  };
};

/**
 * The view page's read: the partner record plus all-time completion totals on every linked
 * collection and tagged asset. Totals ride on the same answer, so the page never shows a row
 * without them; the day-by-day series is read separately, when the insights view asks.
 */
export const transformPartnerInsights = (
  fragment: FragmentType<typeof adminPartnerFragment> & FragmentType<typeof adminPartnerInsightsFragment>,
): PartnerInsights => {
  const partner = transformPartner(fragment);
  const insights = useFragment(adminPartnerInsightsFragment, fragment);

  return {
    ...partner,
    learningAssetCollections: insights.learningAssetCollections.map(collection => ({
      id: collection.id,
      name: collection.name,
      publishedAt: collection.publishedAt ?? null,
      stats: transformCompletableStats(collection.stats),
      learningAssets: collection.learningAssets.map(toAssetInsight),
    })),
    learningAssets: insights.learningAssets.map(toAssetInsight),
  };
};

const transformCompletionSeries = (fragment: FragmentType<typeof adminCompletionSeriesFragment>): CompletionSeries => {
  const series = useFragment(adminCompletionSeriesFragment, fragment);

  return {
    completionsByDay: series.completionsByDay.map(day => ({ date: String(day.date), completions: day.completions })),
    peakDay: series.peakDay ? { date: String(series.peakDay.date), completions: series.peakDay.completions } : null,
  };
};

/**
 * Every collection's and asset's completion series, keyed by id. An asset reachable only through
 * a collection is keyed too, so whichever row the insights view opens on has its series.
 */
export const transformPartnerInsightSeries = (fragment: FragmentType<typeof adminPartnerInsightSeriesFragment>): PartnerInsightSeries => {
  const partner = useFragment(adminPartnerInsightSeriesFragment, fragment);
  const assets: PartnerInsightSeries['assets'] = {};
  const collections: PartnerInsightSeries['collections'] = {};

  for (const collection of partner.learningAssetCollections) {
    collections[collection.id] = transformCompletionSeries(collection.stats);
    for (const asset of collection.learningAssets) {
      assets[asset.id] = transformCompletionSeries(asset.stats);
    }
  }
  for (const asset of partner.learningAssets) {
    assets[asset.id] = transformCompletionSeries(asset.stats);
  }

  return { collections, assets };
};

export const transformPartnerListItem = (fragment: FragmentType<typeof adminPartnerListFragment>): Partner => {
  return useFragment(adminPartnerListFragment, fragment);
};

const toPartnerReference = (
  partner: AdminPartnerReferenceFieldsFragment | PartnerReferenceFieldsFragment,
  meta: PartnerMeta | null,
  iconFile: AdminPartnerFileFieldsFragment | PublicFileFieldsFragment | null,
): Partner => {
  const { mapPublicFragmentToUpload } = useV2CoreFileUpload();

  return {
    ...partner,
    iconFile: iconFile ? mapPublicFragmentToUpload(iconFile) : null,
    meta,
  };
};

export const transformAdminPartnerReference = (fragment: FragmentType<typeof adminPartnerReferenceFragment>): Partner => {
  const partner = useFragment(adminPartnerReferenceFragment, fragment);

  return toPartnerReference(
    partner,
    partner.meta ? useFragment(adminPartnerMetaFragment, partner.meta) : null,
    partner.iconFile ? useFragment(adminPartnerFileFragment, partner.iconFile) : null,
  );
};

export const transformPartnerReference = (fragment: FragmentType<typeof partnerReferenceFragment>): Partner => {
  const partner = useFragment(partnerReferenceFragment, fragment);

  return toPartnerReference(
    partner,
    partner.meta ? useFragment(partnerMetaFragment, partner.meta) : null,
    partner.iconFile ? useFragment(publicFileFragment, partner.iconFile) : null,
  );
};
